import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined"
import Chip from "@mui/material/Chip"
import DialogContent from "@mui/material/DialogContent"
import Divider from "@mui/material/Divider"
import Drawer from "@mui/material/Drawer"
import IconButton from "@mui/material/IconButton"
import Skeleton from "@mui/material/Skeleton"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import * as fileDownload from "js-file-download"
import { useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import TitledSection from "../../../common/components/TitledSection"
import { formatDateTime } from "../../../common/utils"
import DialogHeader from "../components/DialogHeader"

const STATUS_CHIP = {
  completed: { label: "Completed", color: "success" },
  failed: { label: "Failed", color: "error" },
  processing: { label: "Processing", color: "warning" },
  queued: { label: "Queued", color: "warning" },
}

export default function ImportHistoryDrawer({
  open,
  onClose,
  history,
  isLoading,
  onDownloadError,
}) {
  const axios = useAuthorizedAxiosClient()
  const [downloadingJobId, setDownloadingJobId] = useState(null)
  const jobs = history?.results || []

  function handleDownloadClicked(job) {
    if (!job.has_file) {
      return
    }
    setDownloadingJobId(job.id)
    axios
      .get(`/crm/api/uploaded-leads/import-jobs/${job.id}/file/`, {
        responseType: "blob",
      })
      .then(({ data }) => {
        const filename = job.original_filename || `upload-${job.id}.csv`
        fileDownload(
          data,
          filename.endsWith(".csv") ? filename : `${filename}.csv`
        )
      })
      .catch(() => {
        onDownloadError?.("The original upload file is not available.")
      })
      .finally(() => setDownloadingJobId(null))
  }

  return (
    <Drawer
      open={open}
      onClose={() => onClose()}
      anchor="right"
      sx={{ zIndex: 1201 }}
      PaperProps={{
        sx: { zIndex: 1201 },
      }}
    >
      <DialogHeader title="Upload History" onClose={onClose} />
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          pr: 1,
        }}
      >
        <TitledSection title="Summary">
          <Row sx={{ alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Chip
              size="small"
              color="primary"
              label={`${(history?.upload_count || 0).toLocaleString()} uploads`}
            />
            <Chip
              size="small"
              color="success"
              variant="outlined"
              label={`${(
                history?.success_count || 0
              ).toLocaleString()} success`}
            />
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label={`${(history?.error_count || 0).toLocaleString()} errors`}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`${(
                history?.skipped_count || 0
              ).toLocaleString()} skipped`}
            />
          </Row>
        </TitledSection>
        <Divider sx={{ my: 3 }} />
        <TitledSection title="Uploads">
          {isLoading ? (
            <Column sx={{ gap: 2 }}>
              <Skeleton variant="rectangular" height={72} />
              <Skeleton variant="rectangular" height={72} />
            </Column>
          ) : jobs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No uploads yet. Use Import Leads to upload a CSV file.
            </Typography>
          ) : (
            <Column sx={{ gap: 2 }}>
              {jobs.map((job, index) => {
                const status = STATUS_CHIP[job.status] || {
                  label: job.status,
                  color: "default",
                }
                return (
                  <Column key={job.id} sx={{ gap: 1 }}>
                    {index > 0 ? <Divider /> : null}
                    <Row
                      sx={{
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Column sx={{ gap: 0.5, minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          noWrap
                          title={job.upload_name || job.original_filename}
                        >
                          {job.upload_name ||
                            job.original_filename ||
                            `Upload #${job.id}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {job.upload_name && job.original_filename
                            ? job.original_filename
                            : job.created
                            ? formatDateTime(job.created)
                            : "—"}
                        </Typography>
                        {job.upload_name && job.original_filename ? (
                          <Typography variant="body2" color="text.secondary">
                            {job.created ? formatDateTime(job.created) : "—"}
                          </Typography>
                        ) : null}
                      </Column>
                      <Row sx={{ alignItems: "center", gap: 0.5 }}>
                        <Chip
                          size="small"
                          color={status.color}
                          label={status.label}
                        />
                        <Tooltip
                          title={
                            job.has_file
                              ? "Download uploaded file"
                              : "Original file is not available"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={
                                !job.has_file || downloadingJobId === job.id
                              }
                              onClick={() => handleDownloadClicked(job)}
                            >
                              <DownloadOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Row>
                    </Row>
                    <Row
                      sx={{ alignItems: "center", gap: 1, flexWrap: "wrap" }}
                    >
                      <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        label={`${(
                          job.created_count || 0
                        ).toLocaleString()} success`}
                      />
                      <Chip
                        size="small"
                        color="error"
                        variant="outlined"
                        label={`${(
                          job.failed_count || 0
                        ).toLocaleString()} errors`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${(
                          job.skipped_count || 0
                        ).toLocaleString()} skipped`}
                      />
                      {job.dnc_matched_count ? (
                        <Chip
                          size="small"
                          color="error"
                          variant="outlined"
                          label={`${job.dnc_matched_count.toLocaleString()} DNC`}
                        />
                      ) : null}
                    </Row>
                    {job.error_message ? (
                      <Typography variant="body2" color="error">
                        {job.error_message}
                      </Typography>
                    ) : null}
                  </Column>
                )
              })}
            </Column>
          )}
        </TitledSection>
      </DialogContent>
    </Drawer>
  )
}
